import {BaseComponent} from './base-component.js';
import {customElement, property} from 'lit/decorators.js';
import {html, css, CSSResultGroup, TemplateResult} from 'lit';

export enum TAG_VARIANT {
  GRAY = 'gray',
  THEME_TEXT = 'theme-text',
  THEME_FILL = 'theme-fill',
  WARNING = 'warning',
  SUCCESS = 'success',
}

export enum TAG_SIZE {
  SMALL = 'small',
  BASE = 'base',
}

export enum TAG_VERSION {
  BOLD = 'bold',
  REGULAR = 'regular',
  DETAILS = 'details',
}

@customElement('il-tag')
export default class TagComponent extends BaseComponent {
  @property({type: String})
  label: string = '';

  @property({type: String})
  variant: TAG_VARIANT = TAG_VARIANT.GRAY;

  @property({type: String})
  size: TAG_SIZE = TAG_SIZE.BASE;

  @property({type: String})
  version: TAG_VERSION = TAG_VERSION.REGULAR;

  @property({type: Boolean, reflect: true})
  fill: boolean = false;

  @property({type: Boolean, reflect: true, attribute: 'skeleton-loading'})
  skeletonLoading: boolean = false;

  @property({type: Object})
  onButtonClick?: () => void;

  static override styles = [
    BaseComponent.styles,
    css`
      .il-tag {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        border-width: 1px;
        border-style: solid;
        border-radius: 4px;
        box-sizing: border-box;
        white-space: nowrap;
      }

      /* Fill variation - makes tag full width */
      :host([fill]) .il-tag {
        display: flex;
        width: 100%;
        justify-content: space-between;
      }

      /* Size variations */
      .il-tag--small {
        padding: 1px 6px;
        font-size: 14px;
        line-height: 140%;
        letter-spacing: -0.2px;
      }

      .il-tag--base {
        padding: 4px 8px;
        font-size: 16px;
        line-height: 125%;
        letter-spacing: -0.2px;
      }

      /* Reduce right padding when button is present (but not for details) */
      .il-tag--small.il-tag--has-button:not(.il-tag--details) {
        padding-right: 2px;
      }

      .il-tag--base.il-tag--has-button:not(.il-tag--details) {
        padding-right: 4px;
      }

      /* Content wrapper */
      .il-tag__content-wrapper {
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }

      /* Weight variations */
      .il-tag--bold {
        font-weight: 700;
      }

      .il-tag--regular {
        font-weight: 400;
      }

      /* Gray variant */
      .il-tag--gray {
        background-color: var(--il-color-neutrals-50);
        border-color: var(--il-color-neutrals-100);
        color: var(--il-color-neutrals-300);
      }

      /* Theme Text variant */
      .il-tag--theme-text {
        background-color: var(--il-color-accent-50);
        border-color: var(--il-color-accent-100);
        color: var(--il-color-accent-300);
      }

      /* Theme Fill variant */
      .il-tag--theme-fill {
        background-color: var(--il-color-accent-300);
        border-color: var(--il-color-accent-300);
        color: var(--il-color-neutrals-50);
      }

      /* Warning variant */
      .il-tag--warning {
        background-color: var(--il-color-neutrals-50);
        border-color: var(--il-color-neutrals-100);
        color: var(--il-color-warning);
      }

      /* Success variant */
      .il-tag--success {
        background-color: var(--il-color-success-50);
        border-color: var(--il-color-success-100);
        color: var(--il-color-success-300);
      }

      /* Action button */
      .il-tag__button {
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        border-radius: 50%;
        padding: 0;
        margin: 0 0 0 4px;
        cursor: pointer;
        color: var(--il-color-accent-300);
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }

      .il-tag--theme-fill .il-tag__button {
        color: var(--il-color-neutrals-0);
      }

      .il-tag--details .il-tag__button {
        margin-right: 4px;
      }

      .il-tag__button:hover {
        opacity: 0.7;
      }

      .il-tag__button svg {
        width: 20px;
        height: 20px;
      }

      .il-tag__button ::slotted(*) {
        width: 20px;
        height: 20px;
      }

      /* Details layout */
      .il-tag--details .il-tag__content {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }

      .il-tag--details .il-tag__label {
        font-size: 14px;
        font-weight: 700;
        line-height: 140%;
        letter-spacing: -0.2px;
      }

      .il-tag--details .il-tag__details {
        font-size: 12px;
        font-weight: 400;
        line-height: 150%;
        letter-spacing: -0.1px;
      }

      .il-tag--theme-text.il-tag--details .il-tag__details {
        color: var(--il-color-neutrals-300);
      }

      .il-tag--theme-fill.il-tag--details .il-tag__details {
        opacity: 0.9;
      }

      /* Skeleton loader */
      .il-tag--skeleton {
        position: relative;
        overflow: hidden;
        pointer-events: none;
        min-width: 80px;
        border-color: transparent;
        background: none;
        color: transparent;
      }


      .il-tag--skeleton::before {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 1;
        border-radius: 4px;
        animation: skeleton-shimmer 1.5s ease-in-out infinite;
        background: linear-gradient(
          90deg,
          var(--il-color-neutrals-100) 25%,
          var(--il-color-neutrals-50) 50%,
          var(--il-color-neutrals-100) 75%
        );
        background-size: 200% 100%;
      }

      .il-tag--small.il-tag--skeleton {
        min-height: 22px;
      }

      .il-tag--base.il-tag--skeleton {
        min-height: 28px;
      }
    `,
  ] as CSSResultGroup;

  override render(): TemplateResult {
    return html`
      <div
        class="
          ${this.baseCSSClass}
          il-tag
          il-tag--${this.variant}
          il-tag--${this.size}
          il-tag--${this.version}
          ${this.onButtonClick ? 'il-tag--has-button' : ''}
          ${this.skeletonLoading ? 'il-tag--skeleton' : ''}
        "
        part="tag"
        data-test-iliad-tag
        data-test-iliad-tag-variant="${this.variant}"
        data-test-iliad-tag-size="${this.size}"
        data-test-iliad-tag-version="${this.version}"
      >
        <div class="il-tag__content-wrapper">
          ${this.renderContent()}
        </div>
        ${this.onButtonClick ? this.renderButton() : ''}
      </div>
    `;
  }

  private renderContent(): TemplateResult {
    if (this.version === TAG_VERSION.DETAILS) {
      return html`
        <div class="il-tag__content">
          <div class="il-tag__label">
            <slot name="label">${this.label}</slot>
          </div>
          <div class="il-tag__details">
            <slot name="details"></slot>
          </div>
        </div>
      `;
    }

    return html`<slot>${this.label}</slot>`;
  }

  private renderButton(): TemplateResult {
    return html`
      <button
        class="il-tag__button"
        @click="${this._onButtonClick}"
        aria-label="Tag action"
        data-test-iliad-tag-button
      >
        <slot name="button-icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.375 15.854L4.146 14.625L8.771 10L4.146 5.375L5.375 4.146L10 8.771L14.625 4.146L15.854 5.375L11.229 10L15.854 14.625L14.625 15.854L10 11.229L5.375 15.854Z"
              fill="currentColor"
            />
          </svg>
        </slot>
      </button>
    `;
  }

  private _onButtonClick(): void {
    if (this.onButtonClick) {
      this.onButtonClick();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'il-tag': TagComponent;
  }
}

